/* eslint-disable react/prop-types */
import * as React from 'react';

import {
  useBoolean,
  useLockFn,
  useMemoizedFn,
  useRequest,
  useSafeState,
} from 'ahooks';
import {
  Badge,
  Button,
  Col,
  Divider,
  Empty,
  Flex,
  Form,
  Modal,
  Popconfirm,
  Row,
  Select,
  Spin,
  Tooltip,
  Typography,
} from 'antd';

import {
  DeleteOutlinedIcon,
  EditOutlinedIcon,
  ExclamationCircleOutlinedIcon,
  InboxOutlinedIcon,
  InfoCircleOutlinedIcon,
  MenuOutlinedIcon,
  PlusOutlinedIcon,
  PrimitiveForm,
  PrimitiveList,
  PrimitiveListItem,
  PrimitiveListItemMeta,
  QuestionCircleFilledIcon,
} from 'components';
import {
  DND_CSS,
  dndArrayMove,
  DndSortableContext,
  useDndSortable,
  useProductAction,
  useProductValue,
} from 'context';
import { searchProducts } from 'services';
import type { ProductComplement, SelectOption } from 'types';
import {
  generateSelectOptions,
  inputRule,
  normalizeToString,
  PATHNAME,
} from 'utils';

const {
  QUERY_PARAM: {
    DEFAULT: {
      SORT_TYPE: DEFAULT_SORT_TYPE,
      PAGE_NUMBER: DEFAULT_PAGE_NUMBER,
      MAX_PAGE_SIZE: DEFAULT_MAX_PAGE_SIZE,
    },
  },
} = PATHNAME;

const MIN_SEARCH_CHARS = 3;

type ProductsParams = Parameters<typeof searchProducts>[number];
type ModalFieldType = Record<
  'complementProduct' | 'complementSubProduct',
  SelectOption
>;

type ProductComplementsInfoItemProps = ProductComplement &
  Record<'openModal', () => void>;

const ProductComplementsInfoItem = React.memo<ProductComplementsInfoItemProps>(
  ({ name, mainSubProduct: { id, identifier }, openModal }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useDndSortable({
      id,
    });
    const { complements = [] } = useProductValue();
    const action = useProductAction();

    const defaultButtonProps: React.ComponentPropsWithoutRef<typeof Button> = {
      type: 'text',
      size: 'small',
    };

    return (
      <PrimitiveListItem
        ref={setNodeRef}
        className='my-2 max-sm:flex-wrap'
        actions={[
          <Tooltip key='delete-complement' title='حذف مکمل'>
            <Popconfirm
              title='تأیید حذف مکمل'
              description='با حذف این مکمل، تمام اطلاعات مرتبط نیز پاک می‌شوند.'
              okButtonProps={{
                className: 'bg-[--ant-color-error]',
              }}
              icon={
                <QuestionCircleFilledIcon className='text-[--ant-color-error]' />
              }
              onConfirm={() => {
                const updatedComplements = complements.filter(
                  ({ mainSubProduct }) => mainSubProduct.id !== id,
                );

                action({
                  type: 'UPDATE_PRODUCT_COMPLEMENTS',
                  payload: {
                    complements: updatedComplements,
                    selectedComplement: undefined,
                  },
                });
              }}
            >
              <Button icon={<DeleteOutlinedIcon />} {...defaultButtonProps} />
            </Popconfirm>
          </Tooltip>,
          <Tooltip key='edit-complement' title='ویرایش مکمل'>
            <Button
              icon={<EditOutlinedIcon />}
              onClick={() => {
                const selectedComplement = complements.find(
                  ({ mainSubProduct }) => mainSubProduct.id === id,
                );

                if (selectedComplement) {
                  action({
                    type: 'UPDATE_PRODUCT_COMPLEMENTS',
                    payload: {
                      complements,
                      selectedComplement,
                    },
                  });
                  openModal();
                }
              }}
              {...defaultButtonProps}
            />
          </Tooltip>,
        ]}
        style={{
          transform: DND_CSS.Transform.toString(transform),
          transition,
          ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
        }}
      >
        <PrimitiveListItemMeta
          avatar={<MenuOutlinedIcon {...attributes} {...listeners} />}
          title={
            <Typography.Text ellipsis>
              <span className='text-sm'>{name}: </span>
              <Badge count={identifier} color='blue' />
            </Typography.Text>
          }
        />
      </PrimitiveListItem>
    );
  },
);
ProductComplementsInfoItem.displayName = 'ProductComplementsInfoItem';

type ProductComplementsInfoModalProps = {
  open: boolean;
  closeModal: () => void;
};

const ProductComplementsInfoModal =
  React.memo<ProductComplementsInfoModalProps>(({ open, closeModal }) => {
    const [form] = Form.useForm<ModalFieldType>();
    const { selectedComplement, complements = [] } = useProductValue();
    const {
      data: productsData,
      error: productsError,
      loading: isProductsLoading,
      run: searchProductsRun,
    } = useRequest(
      async ({ searchKey }: Pick<ProductsParams, 'searchKey'>) =>
        await searchProducts({
          searchKey,
          sortType: DEFAULT_SORT_TYPE,
          pageNumber: DEFAULT_PAGE_NUMBER,
          pageSize: DEFAULT_MAX_PAGE_SIZE,
        }),
      {
        debounceWait: 1000,
        manual: true,
      },
    );
    const [currentSearchKey, setCurrentSearchKey] = useSafeState<string>();
    const [selectedComplementId, setSelectedComplementId] =
      useSafeState<string>();
    const action = useProductAction();

    const { total: totalProducts = 0, data: products = [] } =
      productsData ?? {};
    const { subProducts = [] } =
      products.find(
        product =>
          product.id === (selectedComplementId || selectedComplement?.id),
      ) ?? {};

    React.useEffect(() => {
      if (selectedComplement) {
        const { name } = selectedComplement;

        setCurrentSearchKey(name);
        searchProductsRun({ searchKey: name });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedComplement]);

    const hasValidSearchInput = useMemoizedFn(
      (value: string = '') => value.trim().length >= MIN_SEARCH_CHARS,
    );

    const onCancel = useMemoizedFn(async () => {
      action({
        type: 'UPDATE_PRODUCT_COMPLEMENTS',
        payload: {
          complements,
          selectedComplement: undefined,
        },
      });
      setSelectedComplementId(undefined);
      form.resetFields();
      closeModal();
    });

    const onFinish = useLockFn(
      async ({ complementProduct, complementSubProduct }: ModalFieldType) => {
        const defaultComplements = selectedComplement
          ? complements.filter(
              ({ mainSubProduct: { id: mainSubProductId } }) =>
                mainSubProductId !== selectedComplement.mainSubProduct.id &&
                mainSubProductId !== complementSubProduct.value,
            )
          : complements.filter(
              ({ mainSubProduct: { id: mainSubProductId } }) =>
                mainSubProductId !== complementSubProduct.value,
            );
        const newComplement: NonNullable<typeof selectedComplement> = {
          ...selectedComplement!,
          id: normalizeToString(complementProduct.value || ''),
          name: complementProduct.label as string,
          mainSubProduct: subProducts.find(
            subProduct => subProduct.id === complementSubProduct.value,
          )!,
        };

        action({
          type: 'UPDATE_PRODUCT_COMPLEMENTS',
          payload: {
            complements: [newComplement, ...defaultComplements],
            selectedComplement: undefined,
          },
        });
        setSelectedComplementId(undefined);
        form.resetFields();
        closeModal();
      },
    );

    const productsSelectOptions = generateSelectOptions(
      products,
      'id',
      'name',
      'id',
    );
    const subProductsSelectOptions = generateSelectOptions(
      subProducts,
      'id',
      'identifier',
      'id',
    );

    return (
      <Modal
        centered
        destroyOnClose
        width={950}
        title={selectedComplement ? 'ویرایش مکمل موجود' : 'افزودن مکمل جدید'}
        okText={selectedComplement ? 'ذخیره تغییرات' : 'ساخت مکمل'}
        open={open}
        onOk={form.submit}
        onCancel={onCancel}
      >
        <Divider />
        <PrimitiveForm
          className='max-h-[calc(100dvh-15rem)] overflow-y-auto'
          form={form}
          fields={
            selectedComplement && [
              {
                name: 'complementProduct',
                value: {
                  key: selectedComplement.id,
                  label: selectedComplement.name,
                  value: selectedComplement.id,
                },
              },
              {
                name: 'complementSubProduct',
                value: {
                  key: selectedComplement.mainSubProduct.id,
                  label: selectedComplement.mainSubProduct.identifier,
                  value: selectedComplement.mainSubProduct.id,
                },
              },
            ]
          }
          onFinish={onFinish}
        >
          <Row>
            <Col span={24} lg={12} className='lg:pe-1.5'>
              <Form.Item<ModalFieldType>
                label='محصول'
                extra={`لطفاً برای مشاهده محصول‌ها، حداقل ${MIN_SEARCH_CHARS} کاراکتر جستجو نمایید.`}
                name='complementProduct'
                rules={[inputRule.required]}
              >
                <Select<SelectOption | undefined>
                  allowClear
                  showSearch
                  labelInValue
                  filterOption={false}
                  popupMatchSelectWidth={false}
                  disabled={!!selectedComplement}
                  placeholder='محصول مکمل را انتخاب کنید'
                  searchValue={currentSearchKey}
                  options={
                    hasValidSearchInput(currentSearchKey)
                      ? productsSelectOptions
                      : undefined
                  }
                  notFoundContent={
                    isProductsLoading ? (
                      <div className='grid min-h-24 place-content-center gap-4'>
                        <Spin />
                        <Typography.Text className='text-[--ant-color-info]'>
                          در حال بارگذاری محصول‌ها...
                        </Typography.Text>
                      </div>
                    ) : productsError ? (
                      <div className='grid min-h-24 content-center justify-items-center gap-4'>
                        <ExclamationCircleOutlinedIcon className='text-2xl text-[--ant-color-error]' />
                        <Typography.Text type='danger'>
                          {productsError instanceof Error
                            ? productsError.message
                            : String(productsError)}
                        </Typography.Text>
                      </div>
                    ) : !hasValidSearchInput(currentSearchKey) ? (
                      <div className='grid min-h-24 content-center justify-items-center gap-4'>
                        <InfoCircleOutlinedIcon className='text-2xl' />
                        <Typography.Text type='secondary'>
                          برای مشاهده محصول‌های مورد نظر، لطفاً جستجو نمایید.
                        </Typography.Text>
                      </div>
                    ) : !totalProducts ? (
                      <div className='grid min-h-24 content-center justify-items-center gap-2.5'>
                        <InboxOutlinedIcon className='text-3xl' />
                        <Typography.Text type='secondary'>
                          <span>هیچ محصولی با عنوان</span>
                          <span> &rdquo;{currentSearchKey}&rdquo; </span>
                          <span>وجود ندارد.</span>
                        </Typography.Text>
                      </div>
                    ) : undefined
                  }
                  onChange={value => {
                    setSelectedComplementId(normalizeToString(value?.value));
                    setCurrentSearchKey(normalizeToString(value?.label));
                    form.resetFields(['complementSubProduct']);
                  }}
                  onSearch={value => {
                    if (selectedComplement && !value) return;

                    setCurrentSearchKey(value);
                    if (hasValidSearchInput(value)) {
                      searchProductsRun({ searchKey: value });
                    }
                  }}
                />
              </Form.Item>
            </Col>

            <Col span={24} lg={12} className='lg:ps-1.5'>
              <Form.Item<ModalFieldType>
                label='مدل'
                extra='لطفاً برای مشاهده مدل‌ها، ابتدا محصول مورد نظر را انتخاب کنید.'
                name='complementSubProduct'
                rules={[inputRule.required]}
              >
                <Select
                  allowClear
                  labelInValue
                  maxTagCount='responsive'
                  placeholder='مدل محصول مکمل را انتخاب کنید'
                  loading={isProductsLoading}
                  options={subProductsSelectOptions}
                  filterOption={(inputValue, option) =>
                    normalizeToString(option?.label)
                      .toLowerCase()
                      .includes(inputValue.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
          </Row>
        </PrimitiveForm>
        <Divider />
      </Modal>
    );
  });
ProductComplementsInfoModal.displayName = 'ProductComplementsInfoModal';

const ProductComplementsInfo = () => {
  const [open, { set: setOpen }] = useBoolean();
  const { complements = [] } = useProductValue();
  const action = useProductAction();

  return (
    <Row>
      <Col span={24}>
        <Flex
          align='center'
          justify='space-between'
          gap='small'
          className='max-sm:flex-wrap'
        >
          <Typography.Title level={4} className='mb-0'>
            مکمل‌ها
          </Typography.Title>
          <Button
            className='max-sm:basis-full'
            icon={<PlusOutlinedIcon />}
            onClick={() => setOpen(true)}
          >
            ساخت مکمل
          </Button>
          <ProductComplementsInfoModal
            open={open}
            closeModal={() => setOpen(false)}
          />
        </Flex>

        <div className='my-4 overflow-auto'>
          {complements.length > 0 ? (
            <DndSortableContext
              dnd={{
                onDragEnd: ({ active, over }) => {
                  if (active.id !== over?.id) {
                    const activeIndex = complements.findIndex(
                      ({ mainSubProduct: { id } }) => id === active.id,
                    );
                    const overIndex = complements.findIndex(
                      ({ mainSubProduct: { id } }) => id === over?.id,
                    );
                    const sortedComplements = dndArrayMove(
                      complements,
                      activeIndex,
                      overIndex,
                    );

                    action({
                      type: 'UPDATE_PRODUCT_COMPLEMENTS',
                      payload: {
                        complements: sortedComplements,
                        selectedComplement: undefined,
                      },
                    });
                  }
                },
              }}
              sortable={{
                items: complements.map(({ mainSubProduct: { id = '' } }) => id),
                children: (
                  <PrimitiveList
                    dataSource={complements}
                    renderItem={item => (
                      <ProductComplementsInfoItem
                        openModal={() => setOpen(true)}
                        {...item}
                      />
                    )}
                  />
                ),
              }}
            />
          ) : (
            <Empty
              className='my-20'
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <span className='text-zinc-300'>
                  هنوز مکملی ساخته نشده است.
                </span>
              }
            />
          )}
        </div>
      </Col>
    </Row>
  );
};

export {
  ProductComplementsInfo,
  ProductComplementsInfoItem,
  ProductComplementsInfoModal,
};
